// S4 Hauptsicht — hierarchical phase + task list with drag-drop reorder.
//
// Layout: outer DragDropContext, one droppable for "phases", and for each
// phase one droppable for "tasks". Cross-phase task drag is handled by
// targeting the destination phase's droppable id.
//
// State strategy:
// - Tasks come from the project-detail signal (tasksByPhase), grouped + sorted.
// - Optimistic UI: we mutate locally then PATCH /reorder; failures cause a
//   full refetch (mounted in the catch).
// - Collapsed phases are tracked in a Set inside this component.
//
// Group-by:
// - "phase" (default) renders the existing phase-based DnD layout.
// - "status" / "priority" / "assignee" render phase-like sections built from
//   `bucketTasks`. No drag handles in those modes (cross-bucket reorder makes
//   no sense for those dimensions).

import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import { ChevronDown, ChevronRight, EyeOff, Plus, Search } from "lucide-preact";
import type {
  ProjectWithCounts,
  Task,
  TaskPriority,
  TaskStatus,
} from "@juuplan/shared";
import { useMe } from "../lib/auth";
import { localDateIso, localTodayIso, softPillStyle } from "../lib/format";
import { useCycles } from "../store/cycles";
import {
  createPhase,
  createTask,
  deletePhase,
  deleteTask,
  patchPhase,
  patchTask,
  refreshProjectDetail,
  reorderPhase,
  reorderTask,
} from "../store/project";
import { matchesAllFilters, type FilterRule } from "../lib/viewState";
import { projectTaskToRecord } from "../lib/taskRecord";
import { useLabels, labelsById as buildLabelsById } from "../store/labels";
import { useStatuses } from "../store/statuses";
import { useWorkItemTypes } from "../store/workItemTypes";
import { FilterDisclosure } from "../components/FilterDisclosure";
import { PhaseHeader } from "../components/PhaseHeader";
import { TaskRow } from "../components/TaskRow";
import { TaskModal } from "../components/TaskModal";
import { applyDensity, bootstrapDensity, loadDensity } from "../lib/density";
import { uxEnabled } from "../lib/ux";
import { nextPriority, nextStatusId, useTableKeys } from "../lib/tableKeys";
import { BottomSheet } from "../components/BottomSheet";
import {
  COLUMN_IDS,
  COLUMN_LABEL,
  GRID_GAP_CLASS,
  GRID_PADDING_X_PX,
  frozenMinWidthPx,
  gridMinWidth,
  gridSpacingClass,
  loadHidden,
  saveHidden,
  splitGridTemplate,
  type TableColumnId,
} from "../lib/columns";
import { useLayout } from "../lib/layout";
import {
  bucketTasks,
  GROUP_BY_OPTIONS,
  loadGroupBy,
  saveGroupBy,
  type Bucket,
  type GroupBy,
} from "../lib/grouping";
import {
  loadCollapsedIds,
  saveCollapsedIds,
  loadCollapsedBuckets,
  saveCollapsedBuckets,
} from "../lib/collapseState";
import { batchUpdateTasks, moveTasksToPhase } from "../lib/api";
import { toastError, toastSuccess } from "../lib/toast";
import { QuickAddTask } from "../components/QuickAddTask";
import { BulkActionToolbar } from "../components/BulkActionToolbar";
import {
  toggleId,
  toggleSection,
  isIndeterminate,
  isAllSelected,
  type BulkPatch,
} from "../lib/bulkSelect";

import { Button } from "../components/ui/Button";

interface Props {
  project: ProjectWithCounts;
  tasksByPhase: Record<number, Task[]>;
  /** Viewer-Rolle: alle Edit-Affordances ausgeblendet/deaktiviert. */
  readOnly?: boolean;
  /** Controlled groupBy from parent (SavedViews integration). */
  controlledGroupBy?: GroupBy;
  onGroupByChange?: (gb: GroupBy) => void;
  /** Controlled hidden columns from parent (SavedViews integration). */
  controlledHiddenColumns?: Set<TableColumnId>;
  onHiddenColumnsChange?: (h: Set<TableColumnId>) => void;
  /**
   * Shared view filters from Project.tsx (#872/#873). This view used to own a
   * hand-written predicate AND its own hide-done toggle, which is why the
   * type filter shipped for #872 never reached any project view. Filter
   * SEMANTICS now live once in lib/viewState.ts; what stays local below are only
   * the genuinely view-specific concerns (text search, cycle, quick filters).
   */
  filters?: FilterRule[];
}

type QuickFilter = "all" | "mine" | "this_week" | "overdue";

const QUICK_FILTERS: { id: QuickFilter; label: string }[] = [
  { id: "all", label: "Alle Aufgaben" },
  { id: "mine", label: "Meine Aufgaben" },
  { id: "this_week", label: "Diese Woche fällig" },
  { id: "overdue", label: "Überfällig" },
];

export function TableView(props: Props): preact.JSX.Element {
  const {
    project,
    tasksByPhase,
    readOnly = false,
    controlledGroupBy,
    onGroupByChange,
    controlledHiddenColumns,
    onHiddenColumnsChange,
    filters,
  } = props;
  const me = useMe();
  const cyclesState = useCycles(project.id);
  const [collapsed, setCollapsed] = useState<Set<number>>(() =>
    loadCollapsedIds("phases", project.id)
  );
  /** Collapsed *sub-task* containers — keyed by parent task-id. */
  const [collapsedTasks, setCollapsedTasks] = useState<Set<number>>(() =>
    loadCollapsedIds("tasks", project.id)
  );
  /** Collapsed non-phase buckets — keyed by Bucket.key. */
  const [collapsedBuckets, setCollapsedBuckets] = useState<Set<string>>(() =>
    loadCollapsedBuckets(project.id)
  );
  const [filter, setFilter] = useState<QuickFilter>("all");
  /** Hide completed (status "done") tasks — persisted per project. */
  /** Cycle filter: "all" = no restriction, "none" = tasks without a cycle,
   * a number string = tasks assigned to that specific cycle. */
  const [cycleFilter, setCycleFilter] = useState<"all" | "none" | number>("all");
  const [search, setSearch] = useState("");
  const [openTaskId, setOpenTaskId] = useState<number | null>(null);
  const [internalHiddenColumns, setInternalHiddenColumns] = useState<Set<TableColumnId>>(loadHidden());
  const hiddenColumns = controlledHiddenColumns ?? internalHiddenColumns;
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const columnsMenuRef = useRef<HTMLDivElement>(null);
  const [internalGroupBy, setInternalGroupBy] = useState<GroupBy>(() => loadGroupBy(project.id));
  const groupBy = controlledGroupBy ?? internalGroupBy;
  /** Parent task id under which the inline sub-task add row is open. */
  const [addingSubTaskFor, setAddingSubTaskFor] = useState<number | null>(null);

  // ---------- bulk selection ----------
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  /** Stable callback: flip membership of id in selectedIds. */
  const toggleSelect = useCallback((id: number): void => {
    setSelectedIds((prev) => toggleId(prev, id));
  }, []);

  // Clear selection on Escape.
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setSelectedIds(new Set());
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedIds.size]);

  // Apply persisted density preference once on mount.
  useEffect(() => {
    bootstrapDensity();
  }, []);

  // ---------- UX-Vorschlag P6: verdichtete, tastaturbedienbare Tabelle ----------
  // Nur mit Flag `ux_web_p6`; ohne Flag bleibt alles wie bisher.
  const p6 = uxEnabled("ux_web_p6");
  /** UX-Vorschlag P7 (Mobil-Web): unter 768 px Filter im Bottom-Sheet, kein
   *  „Neue Aufgabe"/„Spalten" in der Werkzeugzeile (der +-Knopf der Seite
   *  öffnet die Schnellerfassung; Spalten gelten mobil ohnehin nicht). */
  const p7 = uxEnabled("ux_web_p7");
  const tableRef = useRef<HTMLDivElement>(null);
  /** Aufgabe, deren Titel gerade per `e` umbenannt wird. */
  const [renamingId, setRenamingId] = useState<number | null>(null);
  // Kompaktdichte ist mit P6 der Standard (nur ohne gespeicherte Wahl —
  // loadDensity() kennt das Flag). Beim Abschalten zurück auf die alte Wahl.
  useEffect(() => {
    applyDensity(loadDensity());
  }, [p6]);

  // Deep-link open: ?task=<id> in the URL (e.g. from a Teams activity
  // notification) opens the matching task modal immediately. We strip the
  // param after consuming it so a manual close doesn't re-open on the next
  // re-render.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("task");
    if (!raw) return;
    const id = Number.parseInt(raw, 10);
    if (Number.isNaN(id)) return;
    setOpenTaskId(id);
    params.delete("task");
    const query = params.toString();
    const next =
      window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
    window.history.replaceState({}, "", next);
  }, [project.id]);

  // Re-load groupBy when the visible project changes — each roadmap has its
  // own remembered grouping. Only applies when not controlled by parent.
  useEffect(() => {
    if (controlledGroupBy === undefined) {
      setInternalGroupBy(loadGroupBy(project.id));
    }
  }, [project.id, controlledGroupBy]);

  // Re-load the collapse/expand layout when switching projects — TableView is
  // not remounted per project, so the lazy initial state above only covers the
  // first mount.
  useEffect(() => {
    setCollapsed(loadCollapsedIds("phases", project.id));
    setCollapsedTasks(loadCollapsedIds("tasks", project.id));
    setCollapsedBuckets(loadCollapsedBuckets(project.id));
  }, [project.id]);

  // Close the columns dropdown on outside click.
  useEffect(() => {
    if (!columnsMenuOpen) return;
    const onDocClick = (e: MouseEvent): void => {
      if (
        columnsMenuRef.current &&
        !columnsMenuRef.current.contains(e.target as Node)
      ) {
        setColumnsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [columnsMenuOpen]);

  const phases = project.phases;

  // Project labels — needed by every TaskRow to resolve task.label_ids.
  const labelsState = useLabels(project.id);
  const labelMap = useMemo(
    () => buildLabelsById(labelsState.items),
    [labelsState.items]
  );

  // Project custom statuses — drive the status cell dropdown + group-by-status.
  const statusesState = useStatuses(project.id);
  const projectStatuses = statusesState.items;

  // Project work-item types — resolve task.type_id for the "Typ" column.
  const workItemTypesState = useWorkItemTypes(project.id);
  const typeMap = useMemo(() => {
    const m = new Map<number, (typeof workItemTypesState.items)[number]>();
    for (const t of workItemTypesState.items) m.set(t.id, t);
    return m;
  }, [workItemTypesState.items]);

  // Flatten + filter -- used both for the modal sibling list and the filter.
  const allTasks = useMemo(() => {
    const out: Task[] = [];
    for (const list of Object.values(tasksByPhase)) out.push(...list);
    return out;
  }, [tasksByPhase]);

  // Compare as local YYYY-MM-DD strings — mixing UTC-parsed date-only values
  // with local Date objects dropped tasks due on the last weekday (a Sunday-due
  // task parsed as UTC midnight lands *after* local Sunday 00:00).
  const todayIso = localTodayIso();
  const endOfWeek = new Date();
  endOfWeek.setDate(endOfWeek.getDate() + (7 - (endOfWeek.getDay() || 7))); // through Sun
  const endOfWeekIso = localDateIso(endOfWeek);

  const matchesFilter = (t: Task): boolean => {
    // Shared, cross-view filters first (type, hide-done, status, priority,
    // assignee, label, due, text). Hide-done used to be a local toggle here;
    // it moved into the shared filters so the Kanban/Calendar/Gantt tabs honour
    // it too — only one view tab is visible at a time, so a control living in
    // this view could never serve the others.
    if (filters && filters.length > 0 && !matchesAllFilters(projectTaskToRecord(t), filters)) {
      return false;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!t.title.toLowerCase().includes(q) && !(t.description ?? "").toLowerCase().includes(q)) {
        return false;
      }
    }
    // Cycle filter — applied regardless of the quick filter.
    if (cycleFilter === "none" && t.cycle_id !== null) return false;
    if (typeof cycleFilter === "number" && t.cycle_id !== cycleFilter) return false;
    if (filter === "all") return true;
    if (filter === "mine")
      return me.user ? t.assignee_ids.includes(me.user.id) : false;
    if (filter === "this_week") {
      if (!t.end_date && !t.deadline) return false;
      const d = (t.deadline ?? t.end_date ?? "").slice(0, 10);
      return d >= todayIso && d <= endOfWeekIso;
    }
    if (filter === "overdue") {
      if (t.status === "done") return false;
      if (!t.deadline && !t.end_date) return false;
      const d = (t.deadline ?? t.end_date ?? "").slice(0, 10);
      return d < todayIso;
    }
    return true;
  };

  const toggle = (id: number): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveCollapsedIds("phases", project.id, next);
      return next;
    });
  };

  const toggleTaskCollapsed = (id: number): void => {
    setCollapsedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveCollapsedIds("tasks", project.id, next);
      return next;
    });
  };

  const toggleBucket = (key: string): void => {
    setCollapsedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveCollapsedBuckets(project.id, next);
      return next;
    });
  };

  const toggleColumn = (id: TableColumnId): void => {
    const next = new Set(hiddenColumns);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    saveHidden(next);
    setInternalHiddenColumns(next);
    if (onHiddenColumnsChange) onHiddenColumnsChange(next);
  };

  const setGroupBy = (gb: GroupBy): void => {
    setInternalGroupBy(gb);
    saveGroupBy(project.id, gb);
    if (onGroupByChange) onGroupByChange(gb);
  };

  // Kopf und Zeilen müssen dieselbe DICHTE fahren: TaskRow rendert je nach
  // Layout-Stil die Komfort- oder die Kompakt-Variante mit anderen
  // Spurbreiten. Der Kopf hing vorher fest auf „komfort" und lief im
  // Kompakt-Layout komplett neben den Zeilen.
  const density = useLayout();
  const { frozen: frozenTemplate, scrolling: scrollingTemplate } =
    splitGridTemplate(hiddenColumns, density);

  const openTask = allTasks.find((t) => t.id === openTaskId) ?? null;

  // ---------- mutations ----------

  const [addingPhase, setAddingPhase] = useState(false);
  const [addingTaskInPhase, setAddingTaskInPhase] = useState<number | null>(null);

  const onPhaseRename = (id: number, name: string): Promise<void> =>
    patchPhase(id, { name });

  const onAddPhase = async (name: string): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await createPhase(project.id, { name: trimmed });
  };

  const onDeletePhase = (id: number): Promise<void> => deletePhase(id);

  const onAddTask = async (phaseId: number, title: string): Promise<void> => {
    const trimmed = title.trim();
    if (!trimmed) return;
    await createTask(phaseId, { title: trimmed });
  };

  const onAddSubTask = async (parent: Task, title: string): Promise<void> => {
    const trimmed = title.trim();
    if (!trimmed) return;
    await createTask(parent.phase_id, { title: trimmed, parent_id: parent.id });
  };

  const taskPatch = (id: number, patch: Parameters<typeof patchTask>[1]): Promise<void> =>
    patchTask(id, patch);

  useTableKeys(tableRef, p6, {
    onSelect: (id) => { if (!readOnly) toggleSelect(id); },
    onOpen: (id) => setOpenTaskId(id),
    onRename: (id) => { if (!readOnly) setRenamingId(id); },
    onStatus: (id) => {
      if (readOnly) return;
      const t = allTasks.find((x) => x.id === id);
      const next = nextStatusId(projectStatuses, t?.statusInfo?.id);
      if (t && next !== null) void taskPatch(id, { status_id: next });
    },
    onPriority: (id) => {
      if (readOnly) return;
      const t = allTasks.find((x) => x.id === id);
      if (t) void taskPatch(id, { priority: nextPriority(t.priority) });
    },
    onSubtask: (id) => {
      if (readOnly) return;
      if (collapsedTasks.has(id)) toggleTaskCollapsed(id);
      setAddingSubTaskFor(id);
    },
  });

  /** P6: Zeile fokussierbar machen (data-task-row) und Umbenennen einblenden. */
  const p6Row = (task: Task, row: preact.JSX.Element): preact.JSX.Element =>
    !p6 ? (
      row
    ) : (
      <div
        data-task-row={task.id}
        tabIndex={-1}
        aria-label={task.title}
        class="juuplan-row-p6 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)]"
      >
        {row}
        {renamingId === task.id && (
          <RenameRow
            value={task.title}
            onSave={async (v) => {
              setRenamingId(null);
              if (v !== task.title) await taskPatch(task.id, { title: v });
              focusRow(task.id);
            }}
            onCancel={() => {
              setRenamingId(null);
              focusRow(task.id);
            }}
          />
        )}
      </div>
    );

  const focusRow = (id: number): void => {
    requestAnimationFrame(() =>
      tableRef.current?.querySelector<HTMLElement>(`[data-task-row="${id}"]`)?.focus()
    );
  };

  const onDeleteTask = async (task: Task): Promise<void> => {
    if (!window.confirm(`Aufgabe „${task.title}" in den Papierkorb verschieben?`)) return;
    try {
      await deleteTask(task.id);
      toastSuccess("Aufgabe gelöscht");
    } catch {
      toastError("Löschen fehlgeschlagen – bitte erneut versuchen");
    }
  };

  /**
   * Resolve a legacy TaskStatus enum value to a status_id from the project's
   * custom statuses by matching category. Falls back to the first status with
   * any matching category, then to the first status overall.
   *
   * Mapping: "done"->"done", "in_progress"->"started", "review"->"review",
   * "backlog"->"backlog", "blocked"->"unstarted" (no blocked category).
   */
  const legacyStatusToId = (v: import("@juuplan/shared").TaskStatus): number | null => {
    const legacyToCategory: Record<import("@juuplan/shared").TaskStatus, string> = {
      done: "done",
      in_progress: "started",
      review: "review",
      backlog: "backlog",
      blocked: "unstarted",
    };
    const cat = legacyToCategory[v];
    const found = projectStatuses.find((s) => s.category === cat) ?? projectStatuses[0];
    return found?.id ?? null;
  };

  // ---------- bulk action ----------

  const applyBulkPatch = async (patch: BulkPatch): Promise<void> => {
    if (bulkBusy || selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = [...selectedIds];
    try {
      const body =
        patch.kind === "status"
          ? { ids, patch: { status_id: patch.status_id } }
          : { ids, patch: { priority: patch.priority } };
      await batchUpdateTasks(body);
      toastSuccess(`${ids.length} ${ids.length === 1 ? "Aufgabe" : "Aufgaben"} aktualisiert`);
      setSelectedIds(new Set());
      void refreshProjectDetail();
    } catch {
      toastError("Massenbearbeitung fehlgeschlagen – keine Änderung gespeichert");
      // Keep selection so user can retry or change their selection.
    } finally {
      setBulkBusy(false);
    }
  };

  /**
   * Bulk-replace the assignee set on every selected task with `ids` (#878).
   * Single project view, so the whole selection is always scoped to
   * `project.id` — see BulkAssigneeAction's `projectIds` prop below.
   */
  const applyBulkAssignees = async (assigneeIds: number[]): Promise<void> => {
    if (bulkBusy || selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = [...selectedIds];
    try {
      await batchUpdateTasks({ ids, patch: { assignee_ids: assigneeIds } });
      toastSuccess(`${ids.length} ${ids.length === 1 ? "Aufgabe" : "Aufgaben"} aktualisiert`);
      setSelectedIds(new Set());
      void refreshProjectDetail();
    } catch {
      toastError("Massenbearbeitung fehlgeschlagen – keine Änderung gespeichert");
    } finally {
      setBulkBusy(false);
    }
  };

  /**
   * Bulk-move the selected tasks into another phase. Phase is not a batch-patch
   * field (see api.moveTasksToPhase), so this uses the per-task /reorder path.
   * Sub-tasks in the selection are skipped server-side (they must follow their
   * parent) and surface as `failed`.
   */
  const applyBulkPhase = async (phaseId: number): Promise<void> => {
    if (bulkBusy || selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = [...selectedIds];
    try {
      const { moved, failed } = await moveTasksToPhase(ids, phaseId);
      const target = phases.find((p) => p.id === phaseId);
      const where = target ? `„${target.name}"` : "Phase";
      if (moved.length > 0) {
        toastSuccess(
          `${moved.length} ${moved.length === 1 ? "Aufgabe" : "Aufgaben"} nach ${where} verschoben` +
            (failed.length > 0 ? ` · ${failed.length} übersprungen` : "")
        );
        setSelectedIds(new Set());
      } else {
        toastError("Verschieben fehlgeschlagen – keine Änderung gespeichert");
      }
      void refreshProjectDetail();
    } catch {
      toastError("Verschieben fehlgeschlagen – bitte erneut versuchen");
    } finally {
      setBulkBusy(false);
    }
  };

  // ---------- drag-drop ----------

  const onDragEnd = async (result: DropResult): Promise<void> => {
    if (!result.destination) return;
    const { source, destination, type, draggableId } = result;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    try {
      if (type === "PHASE") {
        const phaseId = Number(draggableId.replace("phase-", ""));
        await reorderPhase(phaseId, { sort_order: destination.index });
        return;
      }
      // TASK
      const taskId = Number(draggableId.replace("task-", ""));
      const destPhaseId = Number(destination.droppableId.replace("phase-tasks-", ""));
      const body: { phase_id?: number; sort_order: number } = {
        sort_order: destination.index,
      };
      if (destination.droppableId !== source.droppableId) {
        body.phase_id = destPhaseId;
      }
      await reorderTask(taskId, body);
    } catch {
      // Drop optimistic state on the floor — refetch resolves UI back to truth.
      void refreshProjectDetail();
    }
  };

  // ---------- shared row renderer ----------

  /**
   * Render a single top-level task row + its expanded children + the optional
   * inline "+ Sub-Aufgabe" row. Used by both phase-mode (wrapped in <Draggable>)
   * and non-phase grouping (no DnD).
   *
   * @param dragHandleProps  Forwarded from <Draggable>'s render prop; omitted
   *                         in non-phase modes so the grip icon never appears.
   */
  const renderTaskWithChildren = (
    task: Task,
    children: Task[],
    visible: boolean,
    dragHandleProps?: Record<string, unknown>
  ): preact.JSX.Element => {
    const hasChildren = children.length > 0;
    const childrenCollapsed = collapsedTasks.has(task.id);
    const isAddingSub = addingSubTaskFor === task.id;
    return (
      <div class="relative" style={visible ? undefined : { display: "none" }}>
        {/* Sub-task expand/collapse chevron — only when children exist. */}
        {hasChildren && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleTaskCollapsed(task.id);
            }}
            class="absolute left-0 top-1/2 -translate-y-1/2 z-10 inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-lowest)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            aria-label={
              childrenCollapsed
                ? `${children.length} Unteraufgaben einblenden`
                : `${children.length} Unteraufgaben ausblenden`
            }
            aria-expanded={!childrenCollapsed}
            title={
              childrenCollapsed
                ? "Unteraufgaben einblenden"
                : "Unteraufgaben ausblenden"
            }
          >
            {childrenCollapsed ? (
              <ChevronRight size={14} aria-hidden="true" />
            ) : (
              <ChevronDown size={14} aria-hidden="true" />
            )}
            <span class="text-label font-semibold leading-none">
              {children.length}
            </span>
          </button>
        )}
        {p6Row(task, <TaskRow
          task={task}
          onOpen={() => setOpenTaskId(task.id)}
          onTitle={(v) => taskPatch(task.id, { title: v })}
          onStatus={(v: TaskStatus) => {
            const id = legacyStatusToId(v);
            if (id !== null) void taskPatch(task.id, { status_id: id });
          }}
          onStatusId={(id: number) => taskPatch(task.id, { status_id: id })}
          onAssignees={(ids) => taskPatch(task.id, { assignee_ids: ids })}
          onStartDate={(v) =>
            taskPatch(
              task.id,
              v && task.end_date && v > task.end_date
                ? { start_date: v, end_date: v }
                : { start_date: v }
            )
          }
          onEndDate={(v) =>
            taskPatch(
              task.id,
              v && task.start_date && v < task.start_date
                ? { end_date: v, start_date: v }
                : { end_date: v }
            )
          }
          onPriority={(v: TaskPriority | null) => taskPatch(task.id, { priority: v })}
          onDelete={readOnly ? undefined : () => onDeleteTask(task)}
          onAddSubtask={p6 && !readOnly ? () => {
            if (collapsedTasks.has(task.id)) toggleTaskCollapsed(task.id);
            setAddingSubTaskFor(task.id);
          } : undefined}
          dragHandleProps={dragHandleProps}
          projectId={project.id}
          hiddenColumns={hiddenColumns}
          labelsById={labelMap}
          typesById={typeMap}
          statuses={projectStatuses}
          readOnly={readOnly}
          selected={selectedIds.has(task.id)}
          onSelect={readOnly ? undefined : toggleSelect}
          anySelected={selectedIds.size > 0}
        />)}
        {!childrenCollapsed &&
          children.map((child) => (
            <div
              key={child.id}
              class="pl-6 border-l-2 border-[color:var(--color-outline-variant)] ml-3"
            >
              {p6Row(child, <TaskRow
                task={child}
                onOpen={() => setOpenTaskId(child.id)}
                onTitle={(v) => taskPatch(child.id, { title: v })}
                onStatus={(v: TaskStatus) => {
                  const id = legacyStatusToId(v);
                  if (id !== null) void taskPatch(child.id, { status_id: id });
                }}
                onStatusId={(id: number) => taskPatch(child.id, { status_id: id })}
                onAssignees={(ids) => taskPatch(child.id, { assignee_ids: ids })}
                onStartDate={(v) =>
                  taskPatch(
                    child.id,
                    v && child.end_date && v > child.end_date
                      ? { start_date: v, end_date: v }
                      : { start_date: v }
                  )
                }
                onEndDate={(v) =>
                  taskPatch(
                    child.id,
                    v && child.start_date && v < child.start_date
                      ? { end_date: v, start_date: v }
                      : { end_date: v }
                  )
                }
                onPriority={(v: TaskPriority | null) => taskPatch(child.id, { priority: v })}
                onDelete={readOnly ? undefined : () => onDeleteTask(child)}
                projectId={project.id}
                hiddenColumns={hiddenColumns}
                labelsById={labelMap}
                typesById={typeMap}
                statuses={projectStatuses}
                readOnly={readOnly}
                selected={selectedIds.has(child.id)}
                onSelect={readOnly ? undefined : toggleSelect}
                anySelected={selectedIds.size > 0}
              />)}
            </div>
          ))}
        {/* Inline "+ Sub-Aufgabe" — visible when children are expanded (or
            there are no children yet). Same pattern as
            "+ Neue Aufgabe in Phase X" but creates with parent_id. */}
        {!readOnly &&
          (!hasChildren || !childrenCollapsed) &&
          (isAddingSub ? (
            <div class="pl-6 border-l-2 border-[color:var(--color-outline-variant)] ml-3">
              <InlineAddRow
                placeholder={`Sub-Aufgabe in „${task.title}"…`}
                ariaLabel="Sub-Aufgabe"
                onSubmit={async (value) => {
                  await onAddSubTask(task, value);
                  setAddingSubTaskFor(null);
                }}
                onCancel={() => setAddingSubTaskFor(null)}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddingSubTaskFor(task.id)}
              /* P6: ab md steckt die Aktion in den Hover-Aktionen der Zeile (RowActions,
                 Taste `a`) — kein eigener Knopf, der Platz kostet oder die nächste
                 Zeile verdeckt. Mobil bleibt der bisherige Knopf. */
              class={`${p6 ? "ml-6 inline-flex md:hidden" : "ml-6 inline-flex"} items-center gap-1 px-2 py-1 text-label text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors rounded`}
              title={`Sub-Aufgabe in „${task.title}" hinzufügen`}
            >
              <Plus size={14} aria-hidden="true" />
              Sub-Aufgabe
            </button>
          ))}
      </div>
    );
  };

  // ---------- shared header renderer ----------

  /** Header row that matches the data grid template — used by every section.
   *  `sectionEligibleIds` scopes the select-all checkbox to the tasks of *that*
   *  section (phase or bucket) so one header never selects another's rows. */
  const renderHeaderRow = (sectionEligibleIds: number[]): preact.JSX.Element => {
    const allSel = isAllSelected(selectedIds, sectionEligibleIds);
    const indet = isIndeterminate(selectedIds, sectionEligibleIds);
    return (
      <div
        class={`juuplan-table-header hidden md:flex ${gridSpacingClass(density)} py-1.5 text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)] border-b border-[color:var(--color-outline-variant)]`}
        style={{ minWidth: gridMinWidth(hiddenColumns, density) }}
      >
        {/* Eingefrorener Block — deckungsgleich mit dem der Zeilen (gleiche
            Spuren, gleiche Lücke, gleicher Innenabstand aus lib/columns.ts).
            `background-color: inherit` nimmt den Kopf-Hintergrund mit, damit
            beim Waagerecht-Scrollen keine Spalte darunter durchscheint. */}
        <div
          class={`sticky left-0 z-[1] grid grow items-center ${GRID_GAP_CLASS[density]}`}
          style={{
            gridTemplateColumns: frozenTemplate,
            flexBasis: 0,
            minWidth: `${frozenMinWidthPx(density)}px`,
            marginLeft: `-${GRID_PADDING_X_PX[density]}px`,
            paddingLeft: `${GRID_PADDING_X_PX[density]}px`,
            backgroundColor: "inherit",
          }}
        >
          {/* Select-all — Griff-Slot (leer) + Checkbox, damit sie unter den
              Zeilen-Checkboxen sitzt (die Zeilen haben Griff + Checkbox). */}
          <span class="flex items-center gap-1">
            <span class="inline-block shrink-0" style={{ width: "14px" }} aria-hidden="true" />
            <input
              type="checkbox"
              checked={allSel}
              ref={(el) => { if (el) el.indeterminate = indet; }}
              onChange={() => setSelectedIds(toggleSection(selectedIds, sectionEligibleIds))}
              class="size-3.5 shrink-0 accent-[color:var(--color-primary)] cursor-pointer"
              aria-label="Alle Aufgaben dieses Abschnitts auswählen"
            />
          </span>
          <span />
          <span>Titel</span>
        </div>
        <div
          class={`grid shrink-0 items-center ${GRID_GAP_CLASS[density]}`}
          style={{ gridTemplateColumns: scrollingTemplate }}
        >
        {!hiddenColumns.has("ticket") && <span>Ticket</span>}
        {!hiddenColumns.has("type") && <span>Typ</span>}
        {!hiddenColumns.has("status") && <span>Status</span>}
        {!hiddenColumns.has("assignees") && <span>Zuständig</span>}
        {!hiddenColumns.has("labels") && <span class="col-labels">Labels</span>}
        {/* Datumsspalten rechtsbuendig wie ihre Werte (TaskDateCell). */}
        {!hiddenColumns.has("start") && <span class="pr-2 text-right">Start</span>}
        {!hiddenColumns.has("end") && <span class="pr-2 text-right">Ende</span>}
        {!hiddenColumns.has("priority") && <span>Prio</span>}
        <span />
        </div>
      </div>
    );
  };

  // ---------- non-phase grouping renderer ----------

  const buckets: Bucket[] =
    groupBy === "phase" ? [] : bucketTasks(allTasks, groupBy, projectStatuses);

  const renderBucketSection = (bucket: Bucket): preact.JSX.Element => {
    const list = bucket.tasks;
    const topLevel = list.filter((t) => t.parent_id === null);
    // Select-all scoped to this bucket only (see phase mode above).
    const bucketEligibleIds = topLevel
      .filter(matchesFilter)
      .map((t) => t.id);
    const childrenByParent = new Map<number, Task[]>();
    // Children that live in *this* bucket — keeps the visible tree consistent
    // with the bucket criterion. (e.g. when grouped by status, a child with a
    // different status appears in its own bucket, not here.)
    for (const t of list) {
      if (t.parent_id !== null) {
        const arr = childrenByParent.get(t.parent_id) ?? [];
        arr.push(t);
        childrenByParent.set(t.parent_id, arr);
      }
    }
    const isCollapsed = collapsedBuckets.has(bucket.key);
    const totalDone = list.filter((t) => t.status === "done").length;
    const uppercaseGroup = groupBy === "status" || groupBy === "priority";
    const bandStyle = softPillStyle(bucket.color);
    return (
      <section key={bucket.key} class="flex flex-col rounded">
        {/* ClickUp-style group band — matches the /lists TaskListView headers. */}
        <div
          class="flex items-center gap-2 px-3 py-2 rounded-t-md sticky top-0 z-10"
          style={{ background: bandStyle.background, borderLeft: `4px solid ${bucket.color}` }}
        >
          <button
            type="button"
            onClick={() => toggleBucket(bucket.key)}
            class="inline-flex items-center justify-center size-5 rounded hover:bg-[color:var(--color-on-surface)]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            style={{ color: bucket.color }}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? "Gruppe einblenden" : "Gruppe ausblenden"}
            title={isCollapsed ? "Einblenden" : "Ausblenden"}
          >
            {isCollapsed ? (
              <ChevronRight size={16} aria-hidden="true" />
            ) : (
              <ChevronDown size={16} aria-hidden="true" />
            )}
          </button>
          <span
            class={`text-xs font-semibold truncate tracking-wide ${uppercaseGroup ? "uppercase" : ""}`}
            style={{ color: bucket.color }}
          >
            {bucket.label}
          </span>
          <span
            class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-micro font-semibold"
            style={bandStyle}
            title={`${list.length} ${list.length === 1 ? "Aufgabe" : "Aufgaben"}${totalDone > 0 ? ` · ${totalDone} erledigt` : ""}`}
          >
            {list.length}
          </span>
        </div>
        {!isCollapsed && (
          <div class="rounded-b-md border border-t-0 border-[color:var(--color-outline-variant)] overflow-x-auto">
            {renderHeaderRow(bucketEligibleIds)}
            {list.length === 0 ? (
              <p class="px-4 py-3 text-xs text-[color:var(--color-on-surface-variant)] italic">
                Keine Aufgaben in dieser Gruppe
              </p>
            ) : (
              topLevel.map((task) => {
                const children = childrenByParent.get(task.id) ?? [];
                const hasChildren = children.length > 0;
                const childMatches = children.some(matchesFilter);
                const visible = matchesFilter(task) || (hasChildren && childMatches);
                return (
                  <div key={task.id}>
                    {renderTaskWithChildren(task, children, visible, undefined)}
                  </div>
                );
              })
            )}
            {list.length > 0 && <PhaseFooter tasks={list} />}
          </div>
        )}
      </section>
    );
  };

  return (
    <div ref={tableRef} class="flex flex-col gap-3">
      {/* Monday-style Top-Toolbar: New Task + Search + Filter chips inline. */}
      <div class="flex flex-wrap items-center gap-2 px-1 py-1.5">
        {!readOnly && (
          <Button
            variant="primary"
            size="md"
            type="button"
            onClick={() => {
              const first = phases[0];
              if (first) setAddingTaskInPhase(first.id);
            }}
            disabled={phases.length === 0}
            title={phases.length === 0 ? "Lege zuerst eine Phase an" : undefined}
            class={p7 ? "hidden md:inline-flex" : undefined}
          >
            <Plus size={14} aria-hidden="true" />
            Neue Aufgabe
          </Button>
        )}

        <FilterDisclosure
          sheet={p7 ? BottomSheet : undefined}
          count={
            (search.trim() ? 1 : 0) +
            (cycleFilter !== "all" ? 1 : 0) +
            (filter !== "all" ? 1 : 0)
          }
        >
        <div class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 min-h-[36px] sm:min-h-[28px] focus-within:ring-2 focus-within:ring-[color:var(--color-primary)] focus-within:ring-offset-2 transition-shadow">
          <Search size={14} class="text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
          <input
            type="text"
            value={search}
            onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            placeholder="Suche…"
            class="min-w-0 flex-1 sm:flex-none sm:w-28 2xl:w-40 bg-transparent text-base sm:text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none"
            aria-label="Aufgaben durchsuchen"
          />
        </div>

        <div class="hidden sm:block h-5 w-px bg-[color:var(--color-outline-variant)]" aria-hidden="true" />

        {/* Zyklus-Filter — select matching the Gruppieren-control style */}
        {!cyclesState.loading && cyclesState.items.length > 0 && (
          <label class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] min-h-[28px] hover:bg-[color:var(--color-surface-container-low)] focus-within:ring-2 focus-within:ring-[color:var(--color-primary)] focus-within:ring-offset-2 transition-colors">
            <span class="text-label uppercase tracking-wider">Zyklus</span>
            <select
              value={cycleFilter === "all" ? "all" : cycleFilter === "none" ? "none" : String(cycleFilter)}
              onChange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                if (v === "all") setCycleFilter("all");
                else if (v === "none") setCycleFilter("none");
                else setCycleFilter(Number(v));
              }}
              class="min-w-0 flex-1 sm:flex-none bg-transparent text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none cursor-pointer"
              aria-label="Zyklus filtern"
            >
              <option value="all">Alle Zyklen</option>
              <option value="none">Kein Zyklus</option>
              {cyclesState.items.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {QUICK_FILTERS.map((f) => {
          const active = f.id === filter;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              class={`rounded-full px-2.5 py-1 text-xs font-medium border min-h-[28px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
                active
                  ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary-strong)]"
                  : "border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
              }`}
              aria-pressed={active}
            >
              {f.label}
            </button>
          );
        })}

        {/* Group-by selector — Monday-style "Group by [Phase ▾]". */}
        <label class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] min-h-[28px] hover:bg-[color:var(--color-surface-container-low)] focus-within:ring-2 focus-within:ring-[color:var(--color-primary)] focus-within:ring-offset-2 transition-colors">
          <span class="text-label uppercase tracking-wider">Gruppieren</span>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy((e.target as HTMLSelectElement).value as GroupBy)}
            class="min-w-0 flex-1 sm:flex-none bg-transparent text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none cursor-pointer"
            aria-label="Gruppieren nach"
          >
            {GROUP_BY_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        </FilterDisclosure>

        {/* Hide/Show columns dropdown */}
        <div class={`relative ml-auto${p7 ? " hidden md:block" : ""}`} ref={columnsMenuRef}>
          <button
            type="button"
            onClick={() => setColumnsMenuOpen((v) => !v)}
            class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 min-h-[28px] transition-colors"
            aria-haspopup="menu"
            aria-expanded={columnsMenuOpen}
          >
            <EyeOff size={14} aria-hidden="true" />
            Spalten
            {hiddenColumns.size > 0 && (
              <span class="inline-flex items-center justify-center rounded-full bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)] text-label font-semibold min-w-[16px] h-[16px] px-1">
                {hiddenColumns.size}
              </span>
            )}
            <ChevronDown size={14} aria-hidden="true" />
          </button>
          {columnsMenuOpen && (
            <div
              role="menu"
              class="absolute right-0 top-full mt-1 z-20 w-48 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-2 p-1.5 flex flex-col gap-0.5"
            >
              {COLUMN_IDS.map((id) => {
                const visible = !hiddenColumns.has(id);
                return (
                  <label
                    key={id}
                    class="flex items-center gap-2 px-2 py-1.5 rounded text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={visible}
                      onChange={() => toggleColumn(id)}
                      class="size-3.5 accent-[color:var(--color-primary)]"
                    />
                    <span>{COLUMN_LABEL[id]}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bulk action bar — visible only when tasks are selected. */}
      <BulkActionToolbar
        count={selectedIds.size}
        busy={bulkBusy}
        statuses={projectStatuses}
        onSetStatus={(statusId) => void applyBulkPatch({ kind: "status", status_id: statusId })}
        onSetPriority={(p) => void applyBulkPatch({ kind: "priority", priority: p })}
        onSetAssignees={(ids) => void applyBulkAssignees(ids)}
        projectIds={[project.id]}
        phases={phases}
        onSetPhase={(phaseId) => void applyBulkPhase(phaseId)}
        onClear={() => setSelectedIds(new Set())}
      />

      {/* Either phase-mode (DnD) or non-phase mode (read-only buckets). */}
      {groupBy === "phase" ? (
        <DragDropContext onDragEnd={(r) => void onDragEnd(r)}>
          <Droppable droppableId="phases" type="PHASE">
            {(prov) => (
              <div ref={prov.innerRef} {...prov.droppableProps} class="flex flex-col gap-3">
                {phases.length === 0 ? (
                  <EmptyPhasesState
                    onAddPhase={() => setAddingPhase(true)}
                    readOnly={readOnly}
                  />
                ) : (
                  phases.map((phase, idx) => {
                    const list = tasksByPhase[phase.id] ?? [];
                    // For dnd we only deal with top-level tasks; sub-tasks render
                    // inline under their parent and are not directly draggable.
                    const topLevel = list.filter((t) => t.parent_id === null);
                    const childrenByParent = new Map<number, Task[]>();
                    for (const t of list) {
                      if (t.parent_id !== null) {
                        const arr = childrenByParent.get(t.parent_id) ?? [];
                        arr.push(t);
                        childrenByParent.set(t.parent_id, arr);
                      }
                    }
                    const filtered = list.filter(matchesFilter);
                    // Select-all for *this* phase only — top-level, filter-matching
                    // tasks. Passed to renderHeaderRow so each phase header controls
                    // its own rows, not every phase's (see bulkSelect.toggleSection).
                    const phaseEligibleIds = topLevel
                      .filter(matchesFilter)
                      .map((t) => t.id);
                    const isCollapsed = collapsed.has(phase.id);
                    const totalDone = list.filter((t) => t.status === "done").length;
                    return (
                      <Draggable
                        key={phase.id}
                        draggableId={`phase-${phase.id}`}
                        index={idx}
                        isDragDisabled={readOnly}
                      >
                        {(drag, snap) => (
                          <section
                            ref={drag.innerRef}
                            {...(drag.draggableProps as preact.JSX.HTMLAttributes<HTMLElement>)}
                            class="flex flex-col rounded"
                            style={{
                              // dnd-Transform ZUERST übernehmen, sonst klebt die
                              // Phase nicht am Cursor (der harte style unten würde
                              // ihn sonst überschreiben).
                              ...((drag.draggableProps.style ?? {}) as preact.JSX.CSSProperties),
                              borderLeft: `4px solid ${phase.color ?? "var(--color-primary)"}`,
                              paddingLeft: "0",
                              boxShadow: snap.isDragging
                                ? "0 8px 24px rgba(0,0,0,0.18)"
                                : undefined,
                              background: snap.isDragging
                                ? "var(--color-surface-container-lowest)"
                                : undefined,
                            }}
                          >
                            <PhaseHeader
                              phase={phase}
                              collapsed={isCollapsed}
                              taskTotal={list.length}
                              taskDone={totalDone}
                              onToggle={() => toggle(phase.id)}
                              onRename={(name) => onPhaseRename(phase.id, name)}
                              onAddTask={() => setAddingTaskInPhase(phase.id)}
                              onDelete={() => void onDeletePhase(phase.id)}
                              readOnly={readOnly}
                              dragHandleProps={
                                (drag.dragHandleProps ?? undefined) as
                                  | Record<string, unknown>
                                  | undefined
                              }
                            />
                            {!isCollapsed && (
                              <Droppable
                                droppableId={`phase-tasks-${phase.id}`}
                                type="TASK"
                              >
                                {(taskProv, taskSnap) => (
                                  <div
                                    ref={taskProv.innerRef}
                                    {...taskProv.droppableProps}
                                    class={`mt-1 rounded-xl border border-[color:var(--color-outline-variant)]/40 overflow-x-auto bg-[color:var(--color-surface-container-lowest)] ${
                                      taskSnap.isDraggingOver
                                        ? "bg-[color:var(--color-surface-container-low)]"
                                        : ""
                                    }`}
                                  >
                                    {renderHeaderRow(phaseEligibleIds)}
                                    {list.length === 0 ? (
                                      <p class="px-4 py-3 text-xs text-[color:var(--color-on-surface-variant)] italic">
                                        Noch keine Aufgaben in dieser Phase
                                      </p>
                                    ) : filtered.length === 0 ? (
                                      <p class="px-4 py-3 text-xs text-[color:var(--color-on-surface-variant)] italic">
                                        Keine Aufgaben passen zum Filter
                                      </p>
                                    ) : (
                                      /* Render the full top-level list (so dnd indices stay
                                         contiguous), hide non-matching rows. Each parent's
                                         sub-tasks render inline directly below it. */
                                      topLevel.map((task, taskIdx) => {
                                        const children = childrenByParent.get(task.id) ?? [];
                                        const hasChildren = children.length > 0;
                                        // A parent stays visible if it matches the filter OR any
                                        // of its sub-tasks does. Otherwise filter would hide the
                                        // whole sub-tree even when a child matches.
                                        const childMatches = children.some(matchesFilter);
                                        const visible = matchesFilter(task) || (hasChildren && childMatches);
                                        return (
                                          <Draggable
                                            key={task.id}
                                            draggableId={`task-${task.id}`}
                                            index={taskIdx}
                                            isDragDisabled={readOnly || filter !== "all"}
                                          >
                                            {(tdrag, tsnap) => {
                                              const baseStyle =
                                                (tdrag.draggableProps
                                                  .style as preact.JSX.CSSProperties | undefined) ?? {};
                                              return (
                                                <div
                                                  ref={tdrag.innerRef}
                                                  {...(tdrag.draggableProps as preact.JSX.HTMLAttributes<HTMLDivElement>)}
                                                  style={{
                                                    ...baseStyle,
                                                    display: visible ? baseStyle.display : "none",
                                                  }}
                                                  class={tsnap.isDragging ? "shadow-level-2" : ""}
                                                >
                                                  {renderTaskWithChildren(
                                                    task,
                                                    children,
                                                    true,
                                                    (tdrag.dragHandleProps ?? undefined) as
                                                      | Record<string, unknown>
                                                      | undefined
                                                  )}
                                                </div>
                                              );
                                            }}
                                          </Draggable>
                                        );
                                      })
                                    )}
                                    {taskProv.placeholder}
                                    {/* + Add task per phase */}
                                    {readOnly ? null : addingTaskInPhase === phase.id ? (
                                      <InlineAddRow
                                        placeholder={`Titel der neuen Aufgabe in „${phase.name}"…`}
                                        ariaLabel="Neue Aufgabe"
                                        onSubmit={async (value) => {
                                          await onAddTask(phase.id, value);
                                          setAddingTaskInPhase(null);
                                        }}
                                        onCancel={() => setAddingTaskInPhase(null)}
                                      />
                                    ) : p6 ? null /* P6: ein Hinzufügen-Feld pro Phase — das Schnell-Feld unten */ : (
                                      <button
                                        type="button"
                                        onClick={() => setAddingTaskInPhase(phase.id)}
                                        class="w-full inline-flex items-center gap-1.5 px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors border-t border-[color:var(--color-outline-variant)]/60 min-h-[32px]"
                                      >
                                        <Plus size={14} aria-hidden="true" />
                                        Neue Aufgabe in „{phase.name}"
                                      </button>
                                    )}

                                    {/* K6 QuickAddTask — natural-language fast entry */}
                                    {!readOnly && (
                                      <QuickAddTask
                                        phaseId={phase.id}
                                        projectId={project.id}
                                        onCreated={() => void refreshProjectDetail()}
                                      />
                                    )}

                                    {/* Group footer: Monday-style status breakdown summary. */}
                                    {list.length > 0 && <PhaseFooter tasks={list} />}
                                  </div>
                                )}
                              </Droppable>
                            )}
                          </section>
                        )}
                      </Draggable>
                    );
                  })
                )}
                {prov.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div class="flex flex-col gap-3">
          {buckets.length === 0 ? (
            <p class="rounded border border-dashed border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-4 py-6 text-center text-xs text-[color:var(--color-on-surface-variant)] italic">
              Keine Aufgaben in dieser Gruppierung
            </p>
          ) : (
            buckets.map((b) => renderBucketSection(b))
          )}
        </div>
      )}

      {/* + Add phase (also reachable from the empty-state CTA above) — only
          shown in phase-mode; bucketed modes can't host new phases. */}
      {!readOnly &&
        groupBy === "phase" &&
        (addingPhase ? (
          <InlineAddRow
            placeholder="Name der neuen Phase…"
            ariaLabel="Neue Phase"
            onSubmit={async (value) => {
              await onAddPhase(value);
              setAddingPhase(false);
            }}
            onCancel={() => setAddingPhase(false)}
            class="self-start min-w-[260px]"
          />
        ) : phases.length > 0 ? (
          <button
            type="button"
            onClick={() => setAddingPhase(true)}
            class="self-start inline-flex items-center gap-1.5 rounded border border-dashed border-[color:var(--color-outline-variant)] px-4 py-2 text-sm text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
          >
            <Plus size={14} aria-hidden="true" />
            Neue Phase
          </button>
        ) : null)}

      {openTask && (
        <TaskModal
          task={openTask}
          siblings={allTasks}
          projectId={project.id}
          phases={project.phases}
          onClose={() => setOpenTaskId(null)}
          onDeleted={() => setOpenTaskId(null)}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

/**
 * Compact inline input row used for "+ Neue Phase" / "+ Neue Aufgabe".
 * Enter submits, Esc / blur-without-value cancels. Replaces the previous
 * window.prompt() calls (per Phase 1.3 review).
 */
/** P6: Titel per `e` umbenennen — ↵ speichert, Esc bricht ab. */
function RenameRow(props: {
  value: string;
  onSave: (value: string) => Promise<void> | void;
  onCancel: () => void;
}): preact.JSX.Element {
  const [value, setValue] = useState(props.value);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const v = value.trim();
        if (v) void props.onSave(v);
        else props.onCancel();
      }}
      class="flex items-center gap-2 px-3 py-1.5 border-t border-[color:var(--color-outline-variant)]/60 bg-[color:var(--color-surface-container-low)]/40"
    >
      <input
        ref={inputRef}
        type="text"
        value={value}
        onInput={(e) => setValue((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            props.onCancel();
          }
        }}
        aria-label="Aufgabe umbenennen"
        maxLength={200}
        class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
      />
      <span class="text-micro text-[color:var(--color-on-surface-variant)]">↵ speichern · Esc abbrechen</span>
    </form>
  );
}

function InlineAddRow(props: {
  placeholder: string;
  ariaLabel: string;
  onSubmit: (value: string) => Promise<void> | void;
  onCancel: () => void;
  class?: string;
}): preact.JSX.Element {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (): Promise<void> => {
    const trimmed = value.trim();
    if (!trimmed) {
      props.onCancel();
      return;
    }
    setBusy(true);
    try {
      await props.onSubmit(trimmed);
      setValue("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      class={`flex items-center gap-2 px-3 py-2 border-t border-[color:var(--color-outline-variant)]/60 bg-[color:var(--color-surface-container-low)]/40 ${props.class ?? ""}`}
    >
      <Plus size={14} aria-hidden="true" class="text-[color:var(--color-on-surface-variant)] shrink-0" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onInput={(e) => setValue((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            props.onCancel();
          }
        }}
        onBlur={() => {
          if (!value.trim() && !busy) props.onCancel();
        }}
        disabled={busy}
        placeholder={props.placeholder}
        aria-label={props.ariaLabel}
        maxLength={200}
        class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 transition-colors disabled:opacity-60"
      />
      <button
        type="submit"
        disabled={busy || !value.trim()}
        class="rounded bg-[color:var(--color-primary)] px-2.5 py-1 text-micro font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] disabled:opacity-50 transition-colors"
      >
        {busy ? "…" : "Anlegen"}
      </button>
      <button
        type="button"
        onClick={props.onCancel}
        class="rounded px-2 py-1 text-micro text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] transition-colors"
      >
        Abbrechen
      </button>
    </form>
  );
}

// Group-Footer: kompakte Status-Breakdown unten an jeder Phase
// (Anzahl + Done-Prozent + Mini-Balken pro Status). Mirrors Monday's
// "Sum 1,750$" pattern but für Task-Counts.
function PhaseFooter(props: { tasks: Task[] }): preact.JSX.Element {
  const { tasks } = props;
  const total = tasks.length;
  const counts: Record<TaskStatus, number> = {
    backlog: 0, in_progress: 0, review: 0, done: 0, blocked: 0,
  };
  for (const t of tasks) counts[t.status]++;
  const done = counts.done;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div class="flex items-center justify-between gap-2 px-3 py-1.5 text-label text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)]/60 border-t border-[color:var(--color-outline-variant)]">
      <span class="font-medium">
        {total} {total === 1 ? "Aufgabe" : "Aufgaben"}
        {done > 0 && ` · ${done} erledigt (${pct}%)`}
      </span>
      <span class="flex items-center gap-0.5" aria-hidden="true">
        {(["backlog", "in_progress", "review", "done", "blocked"] as TaskStatus[])
          .filter((s) => counts[s] > 0)
          .map((s) => (
            <span
              key={s}
              class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-label font-semibold tabular-nums"
              style={softPillStyle(`var(--color-status-${s.replace("_", "-")})`)}
              title={`${counts[s]} ${s}`}
            >
              {counts[s]}
            </span>
          ))}
      </span>
    </div>
  );
}

function EmptyPhasesState(props: {
  onAddPhase: () => void;
  readOnly?: boolean;
}): preact.JSX.Element {
  return (
    <div class="rounded-lg border border-dashed border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-6 py-12 text-center">
      <p class="text-base font-medium text-[color:var(--color-on-surface)]">
        Diese Roadmap hat noch keine Phase
      </p>
      <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)]">
        {props.readOnly
          ? "Es wurden noch keine Aufgaben angelegt."
          : "Lege eine erste Phase an, um Aufgaben zu strukturieren."}
      </p>
      {!props.readOnly && (
        <Button
          variant="primary"
          size="lg"
          class="mt-4"
          type="button"
          onClick={props.onAddPhase}
        >
          <Plus size={14} aria-hidden="true" />
          Phase hinzufügen
        </Button>
      )}
    </div>
  );
}
